import { IconCircle } from 'components/Icon';
import LinkOrButton from 'components/LinkOrButton';
import Text from 'components/Text';
import type {
	AnchorHTMLAttributesWithRef,
	ButtonHTMLAttributesWithRef,
} from 'types';
import { cn } from 'utils/classNames';

type AnchorAttrs = AnchorHTMLAttributesWithRef<HTMLAnchorElement>;
type ButtonAttrs = ButtonHTMLAttributesWithRef<HTMLButtonElement>;

export const ARROW_LINK_VARIANTS = ['branded', 'negative', 'positive'] as const;
export type ArrowLinkVariant = (typeof ARROW_LINK_VARIANTS)[number];

interface BaseProps {
	className?: string;
	iconClassName?: string;
	text: string;
	variant?: ArrowLinkVariant;
}

interface AnchorProps extends AnchorAttrs, BaseProps {
	type?: never;
	/** Link URL, results in an anchor instead of a button */
	href: string;
	target?: AnchorAttrs['target'];
}

interface ButtonProps extends BaseProps, ButtonAttrs {
	href?: never;
	onClick: NonNullable<ButtonAttrs['onClick']>;
	target?: never;
}

type Props = AnchorProps | ButtonProps;

/** Link component with arrow icon */
export default function ArrowLink({
	className,
	href,
	iconClassName,
	onClick,
	ref,
	text,
	variant = 'branded',
	...attrs
}: Props) {
	if (!text) {
		return null;
	}
	return (
		<LinkOrButton
			{...attrs}
			ref={ref}
			href={href}
			onClick={onClick}
			className={cn(
				className,
				'group/arrow-link inline-flex items-start align-top',
			)}
		>
			<IconCircle
				name="arrow-forward"
				variant={variant}
				className={cn(
					'mr-2 forced-colors:border',
					variant === 'branded' && 'group-hover/arrow-link:bg-brandHover',
					variant === 'negative' && 'group-hover/arrow-link:bg-negativeHover',
					variant === 'positive' && 'group-hover/arrow-link:bg-positiveHover',
					iconClassName,
				)}
			/>
			<Text
				as="span"
				className={cn(
					'group-hover/arrow-link:underline',
					variant === 'negative' && 'text-negative',
				)}
			>
				{text}
			</Text>
		</LinkOrButton>
	);
}
ArrowLink.displayName = 'ArrowLink';
